import React, { useState } from 'react';
import { LOGIN_PATH } from '../../src/utilities/devex_paths';
import classNames from 'classnames';
import styles from './password_reset.module.scss';
import { checkIfEmailExists, initiatePasswordRequest } from './password_reset.utilities';
import { AlertDataType } from '../components/react/alert_popup/types';
import AlertPopup from '../components/react/alert_popup/alert_popup';
import { GENERIC_ERROR_MESSAGE } from '../components/react/alert_popup/constants';

const looseEmailRegex = /^.+@.+$/;

const PasswordReset: React.FC = () => {
  const [alertData, setAlertData] = useState<AlertDataType | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasFormBeenSubmitted(true);

    if (isEmailValid) {
      checkIfEmailExists(email)
        .then((data) => {
          if (data.valid) {
            try {
              initiatePasswordRequest(email);
              setShowSuccessAlert(true);
            } catch {
              setAlertData({
                type: 'error',
                message: GENERIC_ERROR_MESSAGE,
              });
            }

            return null;
          }

          setAlertData({
            type: 'error',
            message: data.message,
            sticky: true,
          });

          setHasFormBeenSubmitted(false);
          setEmail('');
        });
    }
  };

  const renderForm = (
    <>
      <div className='mt-6'>
        Enter your email address and we&apos;ll send reset instructions shortly.
      </div>
      <form noValidate onSubmit={onSubmit} aria-label='reset password form'>
        <div className='mt-3'>
          <input
            className={
              classNames(
                'form-control',
                !isEmailValid && hasFormBeenSubmitted && 'is-invalid'
              )
            }
            value={email}
            type='text'
            name='reset-password-input'
            aria-label='reset-password-input'
            aria-describedby='reset-password-input'
            placeholder='Enter email address'
            onChange={({ target }) => {
              setEmail(target.value);
              setIsEmailValid(looseEmailRegex.test(target.value));
            }}
          />
          <div className='invalid-feedback'>
            <span className='fal small icon-times me-2' />
            Email must contain a username, an @ and a domain
          </div>
        </div>
        <div className='mt-3 d-flex flex-row-reverse'>
          <input className='btn btn-primary ms-15' type='submit' value='Send instructions' />
          <a className='btn btn-outline-secondary me-15' href={LOGIN_PATH}>Cancel</a>
        </div>
      </form>
    </>
  );

  const renderSuccessAlert = (
    <>
      <div
        className={`
            ${styles.successAlert} alert alert-success mt-6
          `}
        aria-label='Reset password confirmation'
        role='alert'
      >
        <div className='d-flex align-items-center gap-3'>
          <i className='fs-05 icon-check-circle fal' />
          <p className={`fs-5 mb-15 ${styles.alertMessage}`}>
            Please check your email for reset instructions.
          </p>
        </div>
      </div>
      <p className='mt-4'>
        We have sent a secure link to help you create a new password.
      </p>
      <div className='mt-3 d-flex flex-row-reverse'>
        <a className='btn btn-primary' href={LOGIN_PATH}>Log in</a>
      </div>
    </>
  );

  return (
    <>
      <AlertPopup
        alertData={alertData}
        setAlertData={setAlertData}
      />
      {showSuccessAlert ? renderSuccessAlert : renderForm}
    </>
  );
};

export default PasswordReset;
